import { getApiKey } from "../../utils/apiKey";
import { useEffect, useRef, useState } from "react";
import { createGlobalStyle } from "styled-components";
import { Link, useHistory } from "routerCompat";
import { Swiper, SwiperSlide } from "swiper/react";
import ReactSelect from "react-select";
import { Skeleton } from "@mui/material";
import { isLogin } from "../../utils/utils";
import axios from "axios";
import { apiCall_domain } from "../api";
import { PulseLoader } from "react-spinners";

const Headers = {
  Authorization: getApiKey(),
  "content-type": "application/json",
};

const AdvanceSearchModal = ({ open, handleClose }) => {
  const local = localStorage.getItem("splash");
  const splash = JSON.parse(local);
  const history = useHistory();
  const isAuthenticated = isLogin();
  const searchTimeoutRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({
    province_id: 1,
    city_id: 7,
  });

  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [searchMode, setSearchMode] = useState(0);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHistory, setSearchHistory] = useState([]);
  const [rawData, setRawData] = useState({
    parents: [],
    items: [],
  });
  const [parentDisplay, setParentDisplay] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const GlobalStyle = createGlobalStyle`
    body { overflow: ${open ? "hidden" : "auto"} !important; }
  `;

  /* ---------------- API Calls ---------------- */

  const search = (searchWord) => {
    return axios.get(
      `${apiCall_domain}/customer/fullsearch?word=${searchWord}`,
      { headers: Headers },
    );
  };

  const getAdvanceSearch = (type) => {
    return axios.get(
      `${apiCall_domain}/customer/products/advanceSearch?type=${type}${
        type === 2 ? `&feature_id=30` : ""
      }`,
      { headers: Headers },
    );
  };

  const getSearchHistory = () => {
    return axios.get(`${apiCall_domain}/customer/products/search/history`, {
      headers: Headers,
    });
  };

  /* ---------------- Reset on Modal Open ---------------- */

  useEffect(() => {
    if (open) {
      setSearchMode(0);
      setSelectedParent(null);
      setSelectedItem(null);
      setSearchQuery("");
      setRawData({ parents: [], items: [] });
      setDisplayList([]);
      console.log("object");
      setSearchResult([]);
      setShowResult(false);
    }
  }, [open]);

  /* ---------------- Cleanup Timeout on Unmount ---------------- */

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  /* ---------------- Load Advance Search Data ---------------- */

  useEffect(() => {
    if (searchMode === 0 || searchMode === -1) return;

    setLoading(true);
    getAdvanceSearch(searchMode)
      .then((res) => {
        if (searchMode === 1) {
          setRawData({
            parents: res.data.carCategories || [],
            items: res.data.cars || [],
          });
          setParentDisplay(res.data.carCategories || []);
          setDisplayList(res.data.cars || []);
        } else if (searchMode === 2) {
          setRawData({ parents: [], items: res.data.features || [] });
          setDisplayList(res.data.features || []);
        } else if (searchMode === 3) {
          setRawData({ parents: [], items: res.data.brands || [] });
          setDisplayList(res.data.brands || []);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch advance search data:", err);
      })
      .finally(() => setLoading(false));
  }, [searchMode]);

  /* ---------------- Filter Display List Based on Search Query ---------------- */

  useEffect(() => {
    if (!searchQuery.trim()) {
      if (searchMode === 1 && selectedParent && !selectedItem) {
        setDisplayList(
          rawData.items.filter((i) => i.cat_id === selectedParent.id),
        );
      } else if (searchMode === 1 && selectedItem) {
        setDisplayList(selectedItem.types || []);
      }
      return;
    }

    const q = searchQuery.trim().toLowerCase();

    if (searchMode === 1) {
      let filtered = [];
      let filteredParent = [];

      if (selectedItem) {
        filtered = (selectedItem.types || []).filter((item) =>
          item.type.toLowerCase().includes(q),
        );
      } else if (!selectedParent) {
        filteredParent = rawData.parents.filter((item) =>
          item.name.toLowerCase().includes(q),
        );
        filtered = rawData.items.filter((item) =>
          item.name.toLowerCase().includes(q),
        );
      } else {
        filtered = rawData.items
          .filter((i) => i.cat_id === selectedParent.id)
          .filter((item) => item?.name?.toLowerCase().includes(q));
      }

      setParentDisplay(filteredParent);
      setDisplayList(filtered);
    } else {
      setDisplayList(
        rawData.items.filter((item) =>
          (item.name || item.value || "").toLowerCase().includes(q),
        ),
      );
    }
  }, [searchQuery, searchMode, selectedParent, selectedItem, rawData]);

  /* ---------------- Load Initial Data ---------------- */

  useEffect(() => {
    setLocations(splash.cities);
    if (isAuthenticated) {
      setSelectedLocation({
        province_id: splash.customer_info?.province?.id,
        city_id: splash.customer_info?.city?.id,
      });
    }

    getSearchHistory()
      .then((res) => {
        console.log(res.data.data);
        setSearchHistory(res.data.data || []);
      })
      .catch((err) => console.error("Failed to load search history:", err));
  }, []);

  /* ---------------- Handlers ---------------- */

  const handleParentClick = (parent) => {
    setSelectedParent(parent);
    setSelectedItem(null);
    setSearchQuery("");
    if (searchMode === 1) {
      setDisplayList(rawData.items.filter((i) => i.cat_id === parent.id));
    }
  };

  const handleItemClick = (item) => {
    if (searchMode === 1 && item.types?.length > 0) {
      setSelectedItem(item);
      setSearchQuery("");
      setDisplayList(item.types);
    } else {
      navigateToResults(item);
    }
  };

  const handleChildClick = (child) => {
    if (!selectedItem) return;
    navigateToResults(selectedItem, child);
  };

  const handleBack = () => {
    if (selectedItem) {
      setSelectedItem(null);
      setSearchQuery("");
      setDisplayList(
        selectedParent
          ? rawData.items.filter((i) => i.cat_id === selectedParent.id)
          : rawData.items,
      );
    } else if (selectedParent) {
      setSelectedParent(null);
      setSearchQuery("");
      setParentDisplay(rawData.parents || []);
      setDisplayList(rawData.items);
    } else if (searchMode !== 0) {
      setSearchMode(0);
      setSearchQuery("");
      setParentDisplay([]);
      setDisplayList([]);
    }
  };

  const navigateToResultsFromHistory = (item) => {
    const params = new URLSearchParams();
    params.set("city_id", splash?.customer_info?.city?.id || 7);

    if (item.type === 1) {
      params.set("car_id", item.details.car_id);
      params.set("car_brand_id", 1);
      params.set("car_type_id", item.details.type_id);
    } else if (item.type === 2) {
      params.set("feature[30]", item.details.feature_value);
    } else if (item.type === 3) {
      params.set("feature[35]", item.details.feature_value);
    }

    history.push(`/categories/3?${params.toString()}`);
    handleClose();
  };

  const navigateToResults = (item, child = null) => {
    const params = new URLSearchParams();
    params.set("advanceSearch", "true");
    params.set(
      "filter_query",
      searchMode === 1 && child
        ? `${item.name} ${item.types.find((type) => type.id === child.id).type}`
        : searchMode === 2
          ? item.value
          : item.name || getPrefixText(),
    );

    if (searchMode !== 0) params.set("filter_type", searchMode);
    params.set("city_id", selectedLocation.city_id);

    if (searchMode === 1) {
      params.set("car_id", item.id || selectedItem.id);
      params.set("car_brand_id", 1);
      if (child) params.set("car_type_id", child.id);
    } else if (searchMode === 2) {
      params.set("feature[30]", item.value);
    } else if (searchMode === 3) {
      params.set("feature[35]", item.name);
    }

    history.push(`/categories/3?${params.toString()}`);
    handleClose();
  };

  const getDisplayValue = (item) => {
    if (searchMode === 1 && selectedItem) return item.type;
    if (searchMode === 1) return item.name;
    if (searchMode === 2) return item.value;
    return item.name;
  };

  const getPrefixText = () => {
    if (!selectedItem && !selectedParent) return null;

    let text = "";
    if (searchMode === 1) {
      if (selectedItem) {
        text = selectedItem.name;
      } else if (selectedParent) {
        text = selectedParent.name;
      }
    } else if (searchMode === 2 && selectedItem) {
      text = selectedItem.value;
    } else if (searchMode === 3 && selectedItem) {
      text = selectedItem.name;
    }

    return text || null;
  };

  const handleSearch = (e) => {
    const value = e.target.value.trim();

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length < 3) {
      setSearchResult([]);
      setSearching(false);
      setSearchLoading(false);
      setShowResult(false);
      return;
    }

    setSearchLoading(true);
    setSearching(true);
    setShowResult(true);

    searchTimeoutRef.current = setTimeout(() => {
      search(value)
        .then((res) => {
          // API returns object with nested arrays, extract the products
          const results = res.data?.products || [];
          console.log(res.data);
          setSearchResult(results);
        })
        .catch((err) => {
          console.error("Search failed:", err);
          setSearchResult([]);
        })
        .finally(() => {
          setSearchLoading(false);
          setSearching(false);
        });
    }, 800);
  };

  /* ---------------- Render ---------------- */

  return (
    <div className={`advanceSearchModal ${open ? "active" : ""}`}>
      <GlobalStyle />

      <div className="header">
        {(selectedParent || selectedItem || searchMode !== 0) && (
          <i
            className="icon-arrow-right"
            onClick={handleBack}
            style={{ cursor: "pointer", marginLeft: "10px" }}
          />
        )}
        <span onClick={handleClose} style={{ cursor: "pointer" }}>
          خروج
        </span>
      </div>

      <div className="content">
        <div className="container">
          {searchMode === 0 ? (
            <>
              <input
                placeholder="جستجو در کاچار..."
                onChange={handleSearch}
                onFocus={() => setShowResult(true)}
                onBlur={() => {
                  setTimeout(() => setShowResult(false), 200);
                }}
                id="search"
              />

              {showResult && (
                <div className="searchResult ShowResult">
                  {searchLoading ? (
                    <div className="searchLoading">
                      <PulseLoader size={14} color="#831f82" />
                    </div>
                  ) : searchResult.length > 0 ? (
                    <ul>
                      {searchResult.map((item) => (
                        <li key={item.id}>
                          <Link to={`/product/${item.id}`}>{item.name}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : searching ? (
                    <div className="searchLoading">
                      <PulseLoader size={14} color="#831f82" />
                    </div>
                  ) : (
                    <div className="searchPlaceholder">
                      <span className="hint">حداقل 3 کاراکتر وارد کنید</span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="searchWrapper">
              {getPrefixText() && (
                <div className="searchPrefix">
                  <span>{getPrefixText()} :</span>
                </div>
              )}
              <input
                placeholder={
                  selectedItem
                    ? `جستجو در تیپ‌های ${selectedItem.name} ...`
                    : selectedParent
                      ? `جستجو در ${selectedParent.name} ...`
                      : "جستجو در کاچار ..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}
        </div>

        <div className="container">
          <div className="address">
            <div>
              <i className="icon-marker" />
              <span>شهر شما:</span>
            </div>
            <div onClick={() => setSearchMode(-1)}>
              {
                locations.find((c) => c.id === selectedLocation.province_id)
                  ?.name
              }{" "}
              /
              {
                locations
                  .find((c) => c.id === selectedLocation.province_id)
                  ?.cities?.find((c) => c.id === selectedLocation.city_id)?.name
              }
            </div>
          </div>
          <div className="addressHint">
            اطلاعات مکانی شما برای کمک به یافتن مغازه های شهر شما و قیمت مناسب
            استفاده می‌شود
          </div>
        </div>

        {searchMode === 0 ? (
          <>
            <div className="container typeSection">
              <div>خرید تایر بر اساس</div>
              <div className="types">
                <div onClick={() => setSearchMode(1)} className="type">
                  <img src="public/assets/car-1.svg" alt="نوع ماشین" />
                  <span>نوع ماشین</span>
                </div>
                <div onClick={() => setSearchMode(3)} className="type">
                  <img src="public/assets/brand.svg" alt="برند تایر" />
                  <span>برند تایر</span>
                </div>
                <div onClick={() => setSearchMode(2)} className="type">
                  <img src="public/assets/size.svg" alt="سایز تایر" />
                  <span>سایز تایر</span>
                </div>
              </div>
            </div>

            {searchHistory.length > 0 && (
              <div className="container typeSection">
                <div className="advanceSearchList">
                  <div>تاریخچه جستجو ها</div>
                  {searchHistory.map((search, i) => (
                    <span
                      onClick={() => navigateToResultsFromHistory(search)}
                      key={`history-${search.id || i}`}
                      className="item"
                    >
                      {search.query}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : searchMode === -1 ? (
          <div className="container">
            <form onSubmit={(e) => e.preventDefault()}>
              {/* Province Select */}
              <div className="formGroup">
                <label className="required">استان</label>
                <ReactSelect
                  components={{ IndicatorSeparator: () => null }}
                  classNamePrefix="selectBox"
                  placeholder="استان خود را انتخاب کنید"
                  noOptionsMessage={() => "در حال بارگذاری..."}
                  value={
                    locations.find((p) => p.id === selectedLocation.province_id)
                      ? {
                          value: selectedLocation.province_id,
                          label: locations.find(
                            (p) => p.id === selectedLocation.province_id,
                          )?.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    setSelectedLocation({
                      province_id: option.value,
                      city_id: null,
                    });
                  }}
                  options={locations.map((province) => ({
                    value: province.id,
                    label: province.name,
                    cities: province.cities,
                  }))}
                />
              </div>

              {/* City Select */}
              <div className="formGroup">
                <label className="required">شهر</label>
                <ReactSelect
                  components={{ IndicatorSeparator: () => null }}
                  classNamePrefix="selectBox"
                  placeholder="ابتدا استان را انتخاب کنید"
                  noOptionsMessage={() => "ابتدا استان را انتخاب کنید"}
                  isDisabled={!selectedLocation.province_id}
                  value={
                    selectedLocation.city_id
                      ? {
                          value: selectedLocation.city_id,
                          label:
                            locations
                              .find(
                                (p) => p.id === selectedLocation.province_id,
                              )
                              ?.cities?.find(
                                (c) => c.id === selectedLocation.city_id,
                              )?.name || "",
                        }
                      : null
                  }
                  onChange={(option) => {
                    setSelectedLocation((prev) => ({
                      ...prev,
                      city_id: option.value,
                    }));
                  }}
                  options={
                    locations
                      .find((p) => p.id === selectedLocation.province_id)
                      ?.cities?.map((city) => ({
                        value: city.id,
                        label: city.name,
                      })) || []
                  }
                />
              </div>
            </form>

            <div className="location formGroup submitGroup">
              <button
                className="locationBtn"
                onClick={() => setSearchMode(0)}
                disabled={!selectedLocation.city_id}
              >
                ثبت اطلاعات
              </button>
            </div>
          </div>
        ) : (
          <div className="container slider">
            {/* Slider for Car Categories */}
            {searchMode === 1 &&
              !selectedItem &&
              parentDisplay.length > 0 &&
              (loading ? (
                <Swiper
                  loop={false}
                  breakpoints={{
                    300: { slidesPerView: 2 },
                    500: { slidesPerView: 2 },
                    641: { slidesPerView: 2.5 },
                    800: { slidesPerView: 3 },
                    1008: { slidesPerView: 3.5 },
                    1366: { slidesPerView: 9 },
                  }}
                  autoplay={{ delay: 2500 }}
                >
                  {[...Array(10)].map((_, i) => (
                    <SwiperSlide
                      key={`skeleton-${i}`}
                      style={{ padding: "1rem", cursor: "pointer" }}
                    >
                      <Skeleton variant="circular" width={120} height={120} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <Swiper
                  breakpoints={{
                    300: { slidesPerView: 2 },
                    500: { slidesPerView: 3 },
                    641: { slidesPerView: 4 },
                    800: { slidesPerView: 5 },
                    1008: { slidesPerView: 6 },
                    1366: { slidesPerView: 9 },
                  }}
                  loop={parentDisplay.length > 2}
                  autoplay={{ delay: 2500 }}
                >
                  {parentDisplay.map((parent) => (
                    <SwiperSlide
                      key={parent.id}
                      style={{ padding: "1rem", cursor: "pointer" }}
                      onClick={() => handleParentClick(parent)}
                    >
                      <div className="slide">
                        <img
                          src={parent.pic || "/assets/car.svg"}
                          alt={parent.name}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ))}

            <div>
              <div className="title">
                {selectedItem
                  ? `تیپ‌های ${selectedItem.name}`
                  : selectedParent
                    ? `${selectedParent.name}`
                    : searchMode === 1
                      ? "همه ماشین‌ها"
                      : searchMode === 2
                        ? "سایزهای موجود"
                        : "برندهای تایر"}
              </div>

              <div className="advanceSearchList">
                {loading ? (
                  <div style={{ padding: "20px", width: "calc(100% - 10rem)" }}>
                    {[...Array(15)].map((_, i) => (
                      <Skeleton
                        key={`loading-${i}`}
                        variant="text"
                        width="100%"
                        height={48}
                        sx={{ display: "inline-block", margin: "6px 8px" }}
                      />
                    ))}
                  </div>
                ) : (
                  displayList.map((item) => (
                    <span
                      key={item.id}
                      className="item"
                      onClick={() => {
                        if (searchMode === 1 && selectedItem) {
                          handleChildClick(item);
                        } else if (searchMode === 1) {
                          handleItemClick(item);
                        } else {
                          navigateToResults(item);
                        }
                      }}
                    >
                      {getDisplayValue(item)}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceSearchModal;
